import { createAsyncThunk } from '@reduxjs/toolkit';

import axios from 'axios';
import { urlJoinP } from 'url-join-ts';
import { captureMessage } from '@sentry/nextjs';

import { LocationType, SliceNameEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import { buildMapApiParams } from '@app/helpers/map-api-params.helpers';
import { convertGeoJsonToMarkers } from '@app/helpers/map-geojson-to-markers';
import {
    LocationDetailsModel,
    MapAccommodationsRequest,
    MapAccommodationsResponse,
    MarkerDataType,
} from '@app/models';

export async function getLocationDetails(
    geoId: string,
    locationType: LocationType,
): Promise<LocationDetailsModel | null> {
    try {
        const response = await axios.get(
            urlJoinP(environment.SSR_API_HOST, ['location-details', '/']),
            {
                params: { geoId, locationType },
            },
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        captureMessage(`Failed to fetch location details: ${error}`);
        throw error;
    }
}

export const fetchMapAccommodations = createAsyncThunk<
    MapAccommodationsResponse,
    MapAccommodationsRequest
>(
    `${SliceNameEnum.Map}/fetch-accommodations`,
    async (
        {
            checkInDate,
            checkOutDate,
            adultsCount,
            childrenCount,
            childrenAges,
            priceFrom,
            priceTo,
            propertyTypeIds,
            amenityIds,
            provisionIds,
            reservationFeatures,
            rating,
            locationDetails,
        },
        { rejectWithValue },
    ) => {
        try {
            const {
                location: { bounds },
            } = locationDetails.details;

            if (!bounds) {
                return rejectWithValue('No bounds available in location details');
            }

            const params = buildMapApiParams({
                maxLat: bounds.maxLat,
                maxLon: bounds.maxLon,
                minLat: bounds.minLat,
                minLon: bounds.minLon,
                checkInDate,
                checkOutDate,
                adultsCount,
                childrenCount,
                childrenAges,
                priceFrom,
                priceTo,
                propertyTypeIds,
                amenityIds,
                provisionIds,
                reservationFeatures,
                rating,
            });

            const apiUrl = `${environment.NOC_API_END_POINT}/api/map/accommodations`;

            let accommodations: MarkerDataType[] = [];

            try {
                const response = await axios.get(apiUrl, {
                    params,
                });

                const geoJsonData = response.data as GeoJSON.FeatureCollection;
                accommodations = convertGeoJsonToMarkers(geoJsonData);
            } catch (apiError) {
                if (axios.isAxiosError(apiError)) {
                    console.warn(
                        `Map accommodations API returned status: ${apiError.response?.status}`,
                        apiError.message,
                    );
                } else {
                    console.error('Error fetching map accommodations:', apiError);
                }
                return rejectWithValue('Failed to fetch map accommodations');
            }
            return {
                accommodations,
            };
        } catch (err) {
            if (err instanceof Error) {
                return rejectWithValue(err.message);
            } else {
                return rejectWithValue('Failed to fetch map accommodations');
            }
        }
    },
);
